import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '@/ui/components'
import { Badge } from '@/ui/components'
import { UserProfileApi, UserMealStat, UserGearStat } from '@/shared/api/users'
import { useAuth } from '@/modules/auth'

interface UserStatsSectionProps {
  userId: string
}

export function UserStatsSection({ userId }: UserStatsSectionProps) {
  const { getAccessToken } = useAuth()
  const [mealStats, setMealStats] = useState<UserMealStat[]>([])
  const [gearStats, setGearStats] = useState<UserGearStat[]>([])
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    if (!userId) return

    const loadStats = async () => {
      setIsLoading(true)
      try {
        const userProfileApi = new UserProfileApi(getAccessToken)
        const stats = await userProfileApi.getUserStats(userId)
        setMealStats(stats.meals.slice(0, 4)) // Top 4
        setGearStats(stats.gear.slice(0, 4)) // Top 4
      } catch (error) {
      } finally {
        setIsLoading(false)
      }
    }

    loadStats()
  }, [userId, getAccessToken])

  if (isLoading) {
    return (
      <div className="grid md:grid-cols-2 gap-4">
        {[0, 1].map(key => (
          <Card key={key} className="border-border bg-card shadow-none">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base font-semibold text-foreground">
                <div className="h-5 w-5 rounded-full bg-adventure-200 dark:bg-earth-200 animate-pulse" />
                <div className="h-5 w-32 rounded bg-adventure-200 dark:bg-earth-200 animate-pulse" />
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 animate-pulse">
                {[0, 1, 2].map(row => (
                  <div key={row} className="p-4 rounded-lg bg-earth-50 dark:bg-earth-100 space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="h-4 w-1/2 rounded bg-earth-100 dark:bg-earth-200" />
                      <div className="h-4 w-16 rounded bg-earth-100 dark:bg-earth-200" />
                    </div>
                    <div className="h-3 w-3/4 rounded bg-earth-100 dark:bg-earth-200" />
                    <div className="h-3 w-2/3 rounded bg-earth-100 dark:bg-earth-200" />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    )
  }

  return (
    <div className="grid md:grid-cols-2 gap-4">
      {/* Most Cooked Meals */}
      <Card className="border-border bg-card shadow-none">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base font-semibold text-foreground">
            Most Cooked Meals
          </CardTitle>
        </CardHeader>
        <CardContent>
          {mealStats.length === 0 ? (
            <p className="text-sm text-earth-600">No meal cooking stats yet</p>
          ) : (
            <div className="space-y-4">
              {mealStats.map((meal) => (
                <div key={meal.mealName} className="p-4 bg-earth-50 rounded-lg">
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="font-medium text-foreground">{meal.mealName}</h4>
                    <Badge variant="outline">{meal.tripCount} trip{meal.tripCount !== 1 ? 's' : ''}</Badge>
                  </div>
                  <div className="space-y-1">
                    {meal.trips.slice(0, 3).map((trip) => (
                      <Link
                        key={trip.tripId}
                        to={`/trips/${trip.tripId}`}
                        className="block truncate text-sm text-earth-600 hover:text-foreground transition-colors"
                      >
                        {trip.tripName}
                      </Link>
                    ))}
                    {meal.trips.length > 3 && (
                      <p className="text-xs text-earth-600">+{meal.trips.length - 3} more trip{meal.trips.length - 3 !== 1 ? 's' : ''}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Most Volunteered Gear */}
      <Card className="border-border bg-card shadow-none">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base font-semibold text-foreground">
            Most Volunteered Gear
          </CardTitle>
        </CardHeader>
        <CardContent>
          {gearStats.length === 0 ? (
            <p className="text-sm text-earth-600">No gear volunteer stats yet</p>
          ) : (
            <div className="space-y-4">
              {gearStats.map((gear) => (
                <div key={gear.gearName} className="p-4 bg-earth-50 rounded-lg">
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="font-medium text-foreground">{gear.gearName}</h4>
                    <Badge variant="outline">{gear.tripCount} trip{gear.tripCount !== 1 ? 's' : ''}</Badge>
                  </div>
                  <div className="space-y-1">
                    {gear.trips.slice(0, 3).map((trip) => (
                      <Link
                        key={trip.tripId}
                        to={`/trips/${trip.tripId}`}
                        className="block truncate text-sm text-earth-600 hover:text-foreground transition-colors"
                      >
                        {trip.tripName}
                      </Link>
                    ))}
                    {gear.trips.length > 3 && (
                      <p className="text-xs text-earth-600">+{gear.trips.length - 3} more trip{gear.trips.length - 3 !== 1 ? 's' : ''}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

